/**
 * One checked answer behind a topic's status, as a sentence (#496), and the mark that stands for it on the semester timeline
 * (#495). The entries are observations the server read from the learner's own checked homework: what was checked, where, when,
 * and the help that came before. No score, no count. The claim id is on the wire for matching and is never shown.
 *
 * The sheet title is untrusted course text: the visible sentence sets it through `UntrustedText`; an aria-label carries it as
 * an attribute string, which is data, not markup.
 */

import type { CourseTopicsResponse } from '@davinci/contracts';
import type { UiLocale } from '../../i18n/locale.js';
import { UntrustedText } from '../homework/untrusted-text.js';

export type EvidenceEntry = CourseTopicsResponse['topics'][number]['evidence'][number];

interface Words {
  readonly right: string;
  readonly wrong: string;
  readonly review: string;
  readonly overturned: string;
  readonly upheld: string;
  readonly question: (label: string) => string;
  readonly help: Readonly<Record<EvidenceEntry['helpBefore'], string>>;
  readonly held: string;
  /** In-app practice (#440): the lead names what the learner did and the help, so no trailing help clause follows. */
  readonly appAlone: string;
  readonly appHint: string;
  readonly appHelp: string;
  readonly appNotYet: string;
  readonly appQuestion: (label: string) => string;
}

const WORDS: Readonly<Record<UiLocale, Words>> = {
  en: {
    right: 'Correct on ',
    wrong: 'Wrong on ',
    review: 'Under review: ',
    overturned: 'Ruled correct on review: ',
    upheld: 'upheld on review',
    question: (label) => `question ${label}`,
    help: { none: '', rule: 'after a course rule was quoted', hint: 'after a hint', counterexample: 'after a counterexample', solution: 'after the solution was shown' },
    held: 'counts for nothing until decided',
    appAlone: 'On your own in the app: ',
    appHint: 'With a hint in the app: ',
    appHelp: 'With help in the app: ',
    appNotYet: 'Not yet in the app: ',
    appQuestion: (label) => `question ${label} variant`,
  },
  he: {
    right: 'נכון: ',
    wrong: 'שגוי: ',
    review: 'בבדיקה: ',
    overturned: 'נקבע כנכון בבדיקה: ',
    upheld: 'אושר בבדיקה',
    question: (label) => `שאלה ${label}`,
    help: { none: '', rule: 'אחרי שצוטט כלל מהקורס', hint: 'אחרי רמז', counterexample: 'אחרי דוגמה נגדית', solution: 'אחרי שהפתרון הוצג' },
    held: 'לא נחשב עד שיוכרע',
    appAlone: 'לבד באפליקציה: ',
    appHint: 'עם רמז באפליקציה: ',
    appHelp: 'עם עזרה באפליקציה: ',
    appNotYet: 'עדיין לא באפליקציה: ',
    appQuestion: (label) => `שאלה ${label}, וריאציה`,
  },
};

export function formatEntryDate(iso: string, locale: UiLocale): string {
  return new Intl.DateTimeFormat(locale === 'he' ? 'he-IL' : 'en-GB', { day: 'numeric', month: 'short' }).format(new Date(iso));
}

function lead(entry: EvidenceEntry, locale: UiLocale): string {
  const w = WORDS[locale];
  if (entry.role === 'held_for_dispute') return w.review;
  if (entry.source === 'in_app') {
    if (entry.verdict === 'refuted') return w.appNotYet;
    return entry.helpBefore === 'none' ? w.appAlone : entry.helpBefore === 'hint' ? w.appHint : w.appHelp;
  }
  if (entry.ruling === 'overturned') return w.overturned;
  return entry.verdict === 'proved' ? w.right : w.wrong;
}

/** What follows the date: the help that came before, or for a held claim why it counts for nothing. */
function tail(entry: EvidenceEntry, locale: UiLocale): string {
  const w = WORDS[locale];
  if (entry.role === 'held_for_dispute') return ` — ${w.held}`;
  if (entry.source === 'in_app') return '';
  const help = w.help[entry.helpBefore];
  const upheld = entry.ruling === 'upheld' ? ` (${w.upheld})` : '';
  return (help === '' ? '' : `, ${help}`) + upheld;
}

function questionPart(entry: EvidenceEntry, locale: UiLocale): string {
  if (entry.question === null) return '';
  return `, ${entry.source === 'in_app' ? WORDS[locale].appQuestion(entry.question) : WORDS[locale].question(entry.question)}`;
}

/** The visible sentence: the sheet through `UntrustedText`, the date left to right. */
export function EntrySentence(props: { readonly entry: EvidenceEntry; readonly locale: UiLocale }): React.JSX.Element {
  const { entry, locale } = props;
  return (
    <>
      {lead(entry, locale)}
      <UntrustedText text={entry.sheet} />
      {questionPart(entry, locale)}
      {', '}
      <bdi dir="ltr">{formatEntryDate(entry.uploadedAt, locale)}</bdi>
      {tail(entry, locale)}
    </>
  );
}

/** The same sentence as plain text, for an aria-label. */
export function entryLabel(entry: EvidenceEntry, locale: UiLocale): string {
  return `${lead(entry, locale)}${entry.sheet}${questionPart(entry, locale)}, ${formatEntryDate(entry.uploadedAt, locale)}${tail(entry, locale)}`;
}

/** Shape, never colour alone: a filled circle for homework right, a cross for homework wrong, a diamond for the app, an open circle when held. */
export function entryShape(entry: EvidenceEntry): '●' | '✕' | '◆' | '○' {
  if (entry.role === 'held_for_dispute') return '○';
  if (entry.source === 'in_app' && entry.verdict === 'proved') return '◆';
  return entry.verdict === 'proved' || entry.ruling === 'overturned' ? '●' : '✕';
}
